
import React, { createContext } from "react";
import { initializeApp, getApp, getApps } from "firebase/app";
import {
    getDatabase,
    ref,
    query,
    orderByChild,
    equalTo,
} from "firebase/database";
import {
    getAuth,
    initializeAuth,
    FacebookAuthProvider,
    GoogleAuthProvider,
    OAuthProvider,
    signInWithPhoneNumber,
    PhoneAuthProvider,
    RecaptchaVerifier,
    unlink,
    updatePhoneNumber,
    linkWithPhoneNumber,
    getReactNativePersistence,
    browserLocalPersistence,
    browserPopupRedirectResolver
} from "firebase/auth";
import { getStorage, ref as stRef } from "firebase/storage";

// Helper to get React Native persistence (only works in RN environment)
const getRNPersistence = (AsyncStorage) => {
    try {
        // Dynamic import for React Native only
        const { getReactNativePersistence } = require("@firebase/auth/react-native");
        return getReactNativePersistence(AsyncStorage);
    } catch (e) {
        console.log("getReactNativePersistence not available (web environment)");
        return null;
    }
};

const FirebaseContext = createContext(null);

let firebase = {
    app: null,
    database: null,
    auth: null,
    storage: null,
}

const createFullStructure = (app, db, auth, storage, config) => {
    return {
        app: app,
        config: config,
        database: db,
        auth: auth,
        storage: storage,
        authRef: getAuth,
        facebookProvider: new FacebookAuthProvider(),
        googleProvider: new GoogleAuthProvider(),
        appleProvider: new OAuthProvider('apple.com'),
        phoneProvider: new PhoneAuthProvider(auth),
        RecaptchaVerifier: RecaptchaVerifier,
        signInWithPhoneNumber: signInWithPhoneNumber,
        updatePhoneNumber: updatePhoneNumber,
        unlink: unlink,
        linkWithPhoneNumber: linkWithPhoneNumber,
        facebookCredential: (token) => FacebookAuthProvider.credential(token),
        facebookCredentialFromResult: (token) => FacebookAuthProvider.credentialFromResult(token),
        mobileAuthCredential: (verificationId, code) => PhoneAuthProvider.credential(verificationId, code),
        usersRef: ref(db, 'users'),
        bookingRef: ref(db, 'bookings'),
        cancelreasonRef: ref(db, 'cancel_reason'),
        complainRef: ref(db, 'complain'),
        editComplainRef: (id) => ref(db, "complain/" + id),
        settingsRef: ref(db, 'settings'),
        carTypesRef: ref(db, 'cartypes'),
        carTypesEditRef: (id) => ref(db, "cartypes/" + id),
        carDocImage: (id) => stRef(storage, `cartypes/${id}`),
        promoRef: ref(db, 'promos'),
        promoEditRef: (id) => ref(db, "promos/" + id),
        notifyRef: ref(db, "notifications/"),
        notifyEditRef: (id) => ref(db, "notifications/" + id),
        singleUserRef: (uid) => ref(db, "users/" + uid),
        profileImageRef: (uid) => stRef(storage, `users/${uid}/profileImage`),
        verifyIdImageRef: (uid) => stRef(storage, `users/${uid}/verifyIdImage`),
        bookingImageRef: (bookingId, imageType) => stRef(storage, `bookings/${bookingId}/${imageType}`),
        driversRef: query(ref(db, "users"), orderByChild("usertype"), equalTo("driver")),
        driverDocsRef: (uid) => stRef(storage, `users/${uid}/license`),
        driverDocsRefBack: (uid) => stRef(storage, `users/${uid}/licenseBack`),
        singleBookingRef: (bookingKey) => ref(db, "bookings/" + bookingKey),
        requestedDriversRef: (bookingKey) => ref(db, "bookings/" + bookingKey + "/requestedDrivers"),
        referralIdRef: (referralId) => query(ref(db, "users"), orderByChild("referralId"), equalTo(referralId)),
        trackingRef: (bookingId) => ref(db, 'tracking/' + bookingId),
        tasksRef: () => query(ref(db, 'bookings'), orderByChild('status'), equalTo('NEW')),
        singleTaskRef: (uid, bookingId) => ref(db, "bookings/" + bookingId + "/requestedDrivers/" + uid),
        bookingListRef: (uid, role) =>
            role == 'customer' ? query(ref(db, 'bookings'), orderByChild('customer'), equalTo(uid)) :
                (role == 'driver' ?
                    query(ref(db, 'bookings'), orderByChild('driver'), equalTo(uid))
                    :
                    ref(db, 'bookings')
                ),
        chatRef: (bookingId) => ref(db, 'chats/' + bookingId + '/messages'),
        withdrawRef: ref(db, 'withdraws/'),
        languagesRef: ref(db, "languages"),
        languagesEditRef: (id) => ref(db, "languages/" + id),
        walletHistoryRef: (uid) => ref(db, "walletHistory/" + uid),
        userNotificationsRef: (uid) => ref(db, "userNotifications/" + uid),
        userRatingsRef: (uid) => ref(db, "userRatings/" + uid),
        carsRef: (uid, role) => role == 'driver' ?
            query(ref(db, 'cars'), orderByChild('driver'), equalTo(uid))
            : (role == 'fleetadmin' ?
                query(ref(db, 'cars'), orderByChild('fleetadmin'), equalTo(uid))
                : ref(db, 'cars')
            ),
        carAddRef: ref(db, "cars"),
        carEditRef: (id) => ref(db, "cars/" + id),
        carImage: (id) => stRef(storage, `cars/${id}`),
        allLocationsRef: ref(db, "locations"),
        userLocationRef: (uid) => ref(db, "locations/" + uid),
        categoryRef: ref(db, 'categories'),
        categoryEditRef: (id) => ref(db, "categories/" + id),
        categoryImage: (id) => stRef(storage, `categories/${id}`),
        addressRef: (uid, id) => ref(db, "savedAddresses/" + uid + "/" + id),
        addressEditRef: (uid) => ref(db, "savedAddresses/" + uid),
        countryRef: ref(db, 'countries'),
        countryEditRef: (id) => ref(db, "countries/" + id),
        zonesRef: ref(db, 'zones'),
        zoneEditRef: (id) => ref(db, "zones/" + id),
        paymentSettingsRef: ref(db, "payment_settings"),
        editPaymentSettingRef: (id) => ref(db, "payment_settings/" + id),
        addMoneyRef: ref(db, "addmoney"),
        smtpRef: ref(db, "smtpdata")
    }
}

const FirebaseProvider = ({ config, children, AsyncStorage }) => {
    // Use Firebase SDK's getApps() as the single source of truth
    // This properly handles both fresh starts and hot reloads
    const appsExist = getApps().length > 0;

    if (!appsExist) {
        // Firebase app doesn't exist - initialize it
        console.log("[FirebaseProvider] Initializing Firebase (no apps exist)");
        try {
            let app, auth, database, storage;

            app = initializeApp(config);
            try {

                // check if web or mobile app
                if (AsyncStorage && typeof document === "undefined") {
                    auth = initializeAuth(app, {
                        persistence: getRNPersistence(AsyncStorage)
                    });
                } else {
                    auth = getAuth(app);
                }
            } catch (e) {
                // Fallback if auth init failed (e.g. already initialized)
                auth = getAuth(app, {
                    persistence: browserLocalPersistence,
                    browserPopupRedirectResolver: browserPopupRedirectResolver
                });
            }

            database = getDatabase(app);
            storage = getStorage(app);

            // Update the exported firebase object
            firebase = createFullStructure(app, database, auth, storage, config);
            console.log("[FirebaseProvider] Firebase initialized successfully");

        } catch (error) {
            console.error("Firebase Initialization Error:", error);
            return null;
        }
    } else if (!firebase.app) {
        // Firebase SDK app exists but our firebase object needs to be rebuilt
        // This can happen during hot reload
        console.log("[FirebaseProvider] Rebuilding firebase object (app exists but structure missing)");
        try {
            const app = getApp();
            const auth = getAuth(app);
            const database = getDatabase(app);
            const storage = getStorage(app);
            firebase = createFullStructure(app, database, auth, storage, config);
        } catch (error) {
            console.error("Firebase rebuild error:", error);
        }
    }

    return (
        <FirebaseContext.Provider value={firebase}>
            {children}
        </FirebaseContext.Provider>
    )
}

export {
    firebase,
    FirebaseProvider,
    FirebaseContext
}
